import {
	all,
	call,
	delay,
	fork,
	put,
	select,
	takeEvery
} from "@redux-saga/core/effects";
import {
	checkTenantSubscription,
	getExecutionLogs,
	getExecutionLogsFlowSchedule
} from "api/FlowDesignerApis";
import { isValidJsonString } from "helpers/common";
import { highlightNode } from "layouts/Flow Designer/helpers/common";
import { toast } from "react-toastify";
import {
	addExecutionReport,
	clearSelectedLogNodeId,
	setCurrentExecutionReportSessionId,
	setExecutionBatchDateTime,
	setExecutionLogs,
	setFlowExecutionFailed,
	showFlowUsageDialog
} from "redux/actions/flowDesigner/flowExecutionActionCreator";
import { stopFlowExecution } from "redux/actions/flowDesigner/tabsActionCreator";
import {
	CANCEL_FLOW_EXECUTION,
	CLEAR_EXECUTION_BATCH_DATE_TIME,
	EXECUTE_FLOW,
	FETCH_EXECUTION_LOGS,
	FETCH_EXECUTION_LOGS_FLOW_SCHEDULE,
	FILTER_AND_ADD_EXECUTION_REPORT,
	HIDE_CONTEXT_PANEL,
	SET_FLOW_EXECUTION_FAILED_RESET,
	START_FLOW_EXECUTION
} from "redux/constants/flowDesigner";
import { executeFlow, cancelFlowExecution } from "api/ExecutorServiceApi";
/***** WORKERS *****/

function* handleExecuteFlow({ payload }) {
	try {
		const { tenant_id, input } = payload;
		const { showContextPanel } = yield select(
			(state) => state.designer.editorContext
		);
		const { currentSavedFlow } = yield select(
			(state) => state.designer.flows
		);
		yield put({ type: SET_FLOW_EXECUTION_FAILED_RESET });
		yield put(clearSelectedLogNodeId());

		const tenantSubscription = yield call(checkTenantSubscription, tenant_id);
		const flow_settings = isValidJsonString(currentSavedFlow?.flow_settings) ? JSON.parse(currentSavedFlow.flow_settings) : currentSavedFlow.flow_settings ? currentSavedFlow.flow_settings : {};
		const { display_execution_logs } = flow_settings;
		const displayExecutionLogs =
			currentSavedFlow?.shared_status?.toLowerCase() === "active"
				? (display_execution_logs?.active ?? false)
				: (display_execution_logs?.draft ?? true);

		if (tenantSubscription && !tenantSubscription.subscription_disabled) {
			if (!tenantSubscription.allow_execution) {
				yield put(
					showFlowUsageDialog(tenantSubscription.allow_execution_message)
				);
				return;
			}
		}

		yield put({ type: START_FLOW_EXECUTION });
		highlightNode("1", "Success");

		if (showContextPanel) {
			yield put({ type: HIDE_CONTEXT_PANEL });
		}

		const res = yield call(executeFlow, tenant_id, input);
		if (!res) {
			yield put(setFlowExecutionFailed());

			return;
		}

		if (res.message) {
			if (res.messageType) {
				toast[res.messageType](res.message);
			} else {
				toast[res.isSuccessful ? "success" : "error"](res.message);
			}
		}else if(!displayExecutionLogs){
			highlightNode("2", res.isSuccessful ? "Success" : "Error");
			if(!res.isSuccessful) yield put(setFlowExecutionFailed());
			yield put(stopFlowExecution());
			toast[res.isSuccessful ? "success" : "error"](res.message ? res.message : "Flow execution completed.");
		} else if (res.resultObject?.execution_batch_datetime) {
			const batchTime = res.resultObject?.execution_batch_datetime;
			const sessionId = res.resultObject?.session_id;

			// Show execution log report if the flow displayExecutionLogs is true
			if (displayExecutionLogs) {
				yield put(setExecutionBatchDateTime(batchTime));
				yield handleFilterAndAddExecutionReport({
					payload: { batchDateTime: batchTime, sessionId: sessionId }
				});
			}
		}

		// yield put(stopFlowExecution());
	} catch (error) {
		console.error(error);
		yield put(setFlowExecutionFailed());
		yield put(stopFlowExecution());
	}
}

function* handleFilterAndAddExecutionReport({ payload }) {
	const { flowId = "", batchDateTime = "", sessionId = "" } = payload;
	const { currentSavedFlow } = yield select((state) => state?.designer?.flows);
	const { executionReports } = yield select((state) => state?.designer?.flowExecution);

	if (currentSavedFlow) {
		const reportFlowId = flowId || currentSavedFlow.id;
		const reportAlreadyExists = executionReports.some(
			(report) => report.id === reportFlowId && report.time === batchDateTime
		);

		if (reportAlreadyExists) {
			yield put(setCurrentExecutionReportSessionId(sessionId));
			return;
		}

		yield put(
			addExecutionReport({
				name: currentSavedFlow.name,
				id: reportFlowId,
				time: batchDateTime,
				sessionId: sessionId,
				data: []
			})
		);
		yield put(setCurrentExecutionReportSessionId(sessionId));
		yield delay(500);
	}
}

function* handleCancelFlowExecution({ payload }) {
	try {
		const { tenant_id } = payload;
		const { executionBatchDateTime } = yield select(
			(state) => state?.designer?.flowExecution
		);
		yield call(cancelFlowExecution, tenant_id, executionBatchDateTime);
		yield put({ type: CLEAR_EXECUTION_BATCH_DATE_TIME });
	} catch (error) {
		console.error(error);
	}
}

function* handleFetchExecutionLogs({ payload }) {
	try {
		const { tenant_id } = payload;
		const logs = yield call(getExecutionLogs, tenant_id);
		if (logs) {
			yield put(setExecutionLogs(logs));
		}
	} catch (error) {
		console.error(error);
	}
}

function* handleFetchExecutionLogsFlowSchedule({ payload }) {
	try {
		const { tenant_id, item_id } = payload;
		const logs = yield call(getExecutionLogsFlowSchedule, tenant_id, item_id);
		if (logs) {
			yield put(setExecutionLogs(logs));
		}
	} catch (error) {
		console.error(error);
	}
}

/***** WATCHERS *****/

function* watchExecuteFlow() {
	yield takeEvery(EXECUTE_FLOW, handleExecuteFlow);
}

function* watchCancelFlowExecution() {
	yield takeEvery(CANCEL_FLOW_EXECUTION, handleCancelFlowExecution);
}
function* watchFetchExecutionLogs() {
	yield takeEvery(FETCH_EXECUTION_LOGS, handleFetchExecutionLogs);
}
function* watchFetchExecutionLogsFlowSchedule() {
	yield takeEvery(
		FETCH_EXECUTION_LOGS_FLOW_SCHEDULE,
		handleFetchExecutionLogsFlowSchedule
	);
}
function* watchFilterAndAddExecutionReport() {
	yield takeEvery(
		FILTER_AND_ADD_EXECUTION_REPORT,
		handleFilterAndAddExecutionReport
	);
}

export function* rootFlowExecutionSaga() {
	yield all([
		fork(watchExecuteFlow),

		fork(watchCancelFlowExecution),
		fork(watchFetchExecutionLogs),
		fork(watchFetchExecutionLogsFlowSchedule),
		fork(watchFilterAndAddExecutionReport)
	]);
}
